import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  X, TrendingDown, TrendingUp, Minus, AlertCircle, CheckCircle2,
  Sparkles, Package,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Legend,
} from "recharts";

const inr = (n) => n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const RECO_STYLE = {
  switch: { cls: "bg-[hsl(6_60%_97%)] border-[hsl(6_62%_45%)]/40 text-[hsl(6_62%_45%)]", icon: TrendingDown },
  keep: { cls: "bg-[hsl(148_40%_95%)] border-[hsl(var(--forest))]/40 text-[hsl(var(--forest))]", icon: CheckCircle2 },
  review: { cls: "bg-[hsl(38_90%_95%)] border-[hsl(35_75%_38%)]/40 text-[hsl(35_75%_38%)]", icon: Sparkles },
  no_data: { cls: "bg-[hsl(var(--bg-2))] border-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]", icon: AlertCircle },
};

// Distinct colour per benchmark vendor (matches priority order used elsewhere).
const VENDOR_COLOURS = ["#2E5C3E", "#B8862B", "#7C3AED", "#0E7490", "#B45309"];

export default function ItemIntelligencePanel({ itemName, itemId, unit, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get("/staff/procurement/intelligence/item-detail", {
          params: { item_name: itemName, item_id: itemId, unit },
        });
        if (alive) setData(d);
      } catch { if (alive) setData(null); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [itemName, itemId, unit]);

  const rec = data?.recommendation;
  const RecoIcon = rec ? RECO_STYLE[rec.kind]?.icon || AlertCircle : null;

  // Prepare chart data: one series for supplier, one per benchmark vendor
  const trendData = (data?.trend || []).map((t) => ({
    month: t.month,
    Supplier: t.supplier_avg,
    ...(t.benchmarks || {}),
  }));
  const vendorSeries = Array.from(new Set((data?.trend || []).flatMap((t) => Object.keys(t.benchmarks || {}))));

  return (
    <div
      data-testid="item-intel-panel"
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-[hsl(var(--bg))] h-full overflow-y-auto shadow-2xl border-l border-[hsl(var(--rule))]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-[hsl(var(--rule))] bg-[hsl(var(--bg))]">
          <div>
            <div className="eyebrow flex items-center gap-1.5"><Package size={12} /> Item Intelligence</div>
            <h3 className="display text-xl mt-0.5">{itemName}</h3>
            <div className="text-xs text-[hsl(var(--ink-soft))]">Unit basis: <span className="font-mono">{data?.unit || unit || "kg"}</span></div>
          </div>
          <button data-testid="item-intel-close" onClick={onClose} className="p-2 text-[hsl(var(--ink-soft))] hover:bg-[hsl(var(--bg-2))]"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="p-8 text-sm text-[hsl(var(--ink-soft))]">Loading item intelligence…</div>
        ) : !data ? (
          <div className="p-8 text-sm text-[hsl(6_62%_45%)]">Could not load item intelligence.</div>
        ) : (
          <div className="p-4 space-y-6">
            {/* Recommendation */}
            {rec && (
              <div data-testid="item-intel-reco" className={`p-3 border text-xs flex items-center gap-2 ${RECO_STYLE[rec.kind]?.cls || RECO_STYLE.no_data.cls}`}>
                {RecoIcon ? <RecoIcon size={14} /> : null}
                <span>{rec.message}</span>
                {rec.savings_pct != null && rec.savings_pct > 0 && (
                  <span className="ml-auto font-semibold">-{rec.savings_pct}%</span>
                )}
              </div>
            )}

            {/* Current supplier + stats */}
            <section>
              <div className="eyebrow mb-2">Current & recent</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[hsl(var(--rule))] border border-[hsl(var(--rule))]">
                <Stat label="Current supplier" value={data.current?.supplier_name || "—"} sub={data.current?.date} testid="stat-current-supplier" />
                <Stat label="Supplier rate" value={inr(data.current?.supplier_rate)} tone="ink" testid="stat-supplier-rate" />
                <Stat label="Avg purchase" value={inr(data.stats?.avg_purchase_price)} testid="stat-avg" />
                <Stat label="Purchases (12mo)" value={data.stats?.purchase_frequency ?? 0} testid="stat-freq" />
                <Stat label="Lowest" value={inr(data.stats?.lowest_purchase_price)} tone="forest" testid="stat-low" />
                <Stat label="Highest" value={inr(data.stats?.highest_purchase_price)} tone="danger" testid="stat-high" />
                <Stat label="Last purchase" value={inr(data.stats?.last_purchase_price)} testid="stat-last" />
                <Stat label="Best benchmark now" value={inr(bestBench(data)?.price)} sub={bestBench(data)?.vendor_name || "—"} tone={bestBench(data) ? "forest" : "ink"} testid="stat-best-bench" />
              </div>
            </section>

            {/* Benchmark table */}
            <section>
              <div className="eyebrow mb-2">Benchmarks (latest across all vendors)</div>
              <div className="border border-[hsl(var(--rule))]">
                <table className="w-full text-xs">
                  <thead className="bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))] uppercase tracking-wider">
                    <tr>
                      <th className="text-left p-2">Vendor</th>
                      <th className="text-right p-2">Rate</th>
                      <th className="text-left p-2">As of</th>
                      <th className="text-center p-2">Match</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[hsl(var(--rule))]">
                    {(data.benchmarks || []).map((b) => (
                      <tr key={b.vendor_id} data-testid={`item-intel-benchmark-${b.vendor_id}`}>
                        <td className="p-2">{b.vendor_name}</td>
                        <td className="p-2 text-right font-mono">
                          {b.price == null ? <span className="text-[hsl(var(--ink-soft))]">—</span> : inr(b.price)}
                        </td>
                        <td className="p-2 text-[hsl(var(--ink-soft))]">{b.effective_date || "—"}</td>
                        <td className="p-2 text-center">
                          <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                            {b.match_kind === "none" ? "—" : b.match_kind}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Trend chart */}
            <section>
              <div className="eyebrow mb-2">12-month price trend</div>
              {trendData.length === 0 ? (
                <div className="text-xs text-[hsl(var(--ink-soft))] p-4 border border-dashed border-[hsl(var(--rule))]">No purchase or benchmark history yet.</div>
              ) : (
                <div data-testid="item-intel-chart" className="border border-[hsl(var(--rule))] p-3">
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={trendData} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--rule))" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="hsl(var(--ink-soft))" />
                      <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--ink-soft))" />
                      <RTooltip contentStyle={{ fontSize: 11, backgroundColor: "hsl(var(--bg))", border: "1px solid hsl(var(--rule))" }} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Line type="monotone" dataKey="Supplier" stroke="hsl(var(--ink))" strokeWidth={2} dot={{ r: 3 }} />
                      {vendorSeries.map((v, i) => (
                        <Line key={v} type="monotone" dataKey={v} stroke={VENDOR_COLOURS[i % VENDOR_COLOURS.length]} strokeWidth={1.5} strokeDasharray="4 4" dot={{ r: 2 }} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </section>

            {/* Recent purchases */}
            <section>
              <div className="eyebrow mb-2">Recent purchases (up to 20)</div>
              <div className="border border-[hsl(var(--rule))] max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))] uppercase tracking-wider sticky top-0">
                    <tr>
                      <th className="text-left p-2">Date</th>
                      <th className="text-left p-2">PO</th>
                      <th className="text-left p-2">Supplier</th>
                      <th className="text-right p-2">Qty</th>
                      <th className="text-right p-2">Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[hsl(var(--rule))]">
                    {(data.recent_purchases || []).length === 0 ? (
                      <tr><td colSpan={5} className="p-4 text-center text-[hsl(var(--ink-soft))]">No recent purchases.</td></tr>
                    ) : (data.recent_purchases || []).map((r, i) => (
                      <tr key={i}>
                        <td className="p-2 text-[hsl(var(--ink-soft))]">{r.date}</td>
                        <td className="p-2 font-mono">{r.po_number}</td>
                        <td className="p-2">{r.vendor_name}</td>
                        <td className="p-2 text-right font-mono">{r.qty} {r.unit}</td>
                        <td className="p-2 text-right font-mono">{inr(r.rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function bestBench(data) {
  return (data?.benchmarks || [])
    .filter((b) => b.price != null)
    .sort((a, b) => a.price - b.price)[0];
}

function Stat({ label, value, sub, tone, testid }) {
  const cls = tone === "danger" ? "text-[hsl(6_62%_45%)]" : tone === "forest" ? "text-[hsl(var(--forest))]" : "text-[hsl(var(--ink))]";
  return (
    <div data-testid={testid} className="p-3 bg-[hsl(var(--bg))]">
      <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">{label}</div>
      <div className={`mt-1 text-sm font-semibold ${cls}`}>{value}</div>
      {sub && <div className="text-[10px] text-[hsl(var(--ink-soft))] mt-0.5">{sub}</div>}
    </div>
  );
}
